// Payment Service - Stripe Integration
// Handles Pro subscription payments

import { logger } from '../utils/logger';
import { syncSubscriptionToSupabase } from './subscriptionSyncService';

export interface PaymentResult {
  success: boolean;
  subscriptionId?: string;
  planType?: string;
  months?: number;
  error?: string;
}

class PaymentService {
  private stripePublishableKey: string | null = null;
  private stripeLoaded: boolean = false;

  constructor() {
    const env = (typeof process !== 'undefined' ? (process as any).env : {}) || {};
    this.stripePublishableKey = env.REACT_APP_STRIPE_PUBLISHABLE_KEY || env.VITE_STRIPE_PUBLISHABLE_KEY || null;
  }

  // Initialize Stripe (load script)
  async initializeStripe(): Promise<boolean> {
    if (this.stripeLoaded) return true;
    
    if (!this.stripePublishableKey) {
      logger.warn('Stripe publishable key not found. Payment features disabled.');
      return false;
    }

    return new Promise((resolve) => {
      // Check if Stripe is already loaded
      if ((window as any).Stripe) {
        this.stripeLoaded = true;
        resolve(true);
        return;
      }

      // Load Stripe.js script
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => {
        this.stripeLoaded = true;
        logger.info('Stripe.js loaded successfully');
        resolve(true);
      };
      script.onerror = () => {
        logger.error('Failed to load Stripe.js');
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }

  // Create Stripe Checkout session for Weekly, Monthly, Lifetime
  async createCheckoutSession(
    planType: 'weekly' | 'monthly' | 'lifetime'
  ): Promise<PaymentResult> {
    try {
      const env = (typeof process !== 'undefined' ? (process as any).env : {}) || {};
      const apiUrl = env.REACT_APP_API_URL || env.VITE_API_URL || '/api';
      const apiBase = apiUrl.includes('://')
        ? (apiUrl.endsWith('/api') ? apiUrl : apiUrl.replace(/\/$/, '') + '/api')
        : apiUrl;

      const body: { planType: string } = {
        planType,
      };

      const { supabaseAuth } = await import('./supabaseAuth');
      const accessToken = await supabaseAuth.getAccessToken();
      if (!accessToken) {
        logger.warn('Checkout blocked: no Supabase access token.');
        return {
          success: false,
          error: 'Please sign in before starting checkout.'
        };
      }

      // Import CSRF service and get CSRF token for secure request
      const { csrfService } = await import('./csrfService');
      const csrfToken = await csrfService.getCSRFToken();

      const response = await fetch(`${apiBase}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create checkout session' }));
        logger.error('Checkout session API failed:', {
          status: response.status,
          statusText: response.statusText,
          planType,
          error: errorData?.error
        });
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const { sessionId, url: checkoutUrl } = await response.json();

      if (!sessionId && !checkoutUrl) {
        logger.error('Checkout session response missing both sessionId and url.');
        throw new Error('Checkout session could not be started. Please try again.');
      }

      const redirectWithHostedUrl = (): PaymentResult => {
        if (!checkoutUrl) {
          return {
            success: false,
            error: 'Payment system not available. Please try again.'
          };
        }
        window.location.assign(checkoutUrl);
        return { success: true };
      };

      // Stripe-hosted fallback works even if Stripe.js is blocked by CSP/ad blockers.
      if (!this.stripePublishableKey) {
        logger.warn('Stripe publishable key missing. Falling back to hosted checkout URL.');
        return redirectWithHostedUrl();
      }

      const stripeInitialized = await this.initializeStripe();
      if (!stripeInitialized) {
        logger.warn('Stripe.js failed to load. Falling back to hosted checkout URL.');
        return redirectWithHostedUrl();
      }

      if (!sessionId) {
        logger.warn('sessionId missing; falling back to hosted checkout URL.');
        return redirectWithHostedUrl();
      }

      // Redirect to Stripe Checkout
      const Stripe = (window as any).Stripe;
      if (!Stripe) {
        logger.warn('Stripe global missing after script load. Falling back to hosted checkout URL.');
        return redirectWithHostedUrl();
      }

      const stripe = Stripe(this.stripePublishableKey);
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        logger.error('Stripe redirectToCheckout failed:', error);
        return redirectWithHostedUrl();
      }

      return { success: true };
    } catch (error: any) {
      console.error('Checkout session error:', error);
      logger.error('Payment error:', error);
      return {
        success: false,
        error: error.message || 'Payment failed. Please try again.'
      };
    }
  }

  // Handle successful payment (called after Stripe redirect)
  async handlePaymentSuccess(sessionId: string): Promise<PaymentResult> {
    try {
      // Get backend API URL from environment
      const env = (typeof process !== 'undefined' ? (process as any).env : {}) || {};
      const apiUrl = env.REACT_APP_API_URL || env.VITE_API_URL || '/api';
      const apiBase = apiUrl.includes('://')
        ? (apiUrl.endsWith('/api') ? apiUrl : apiUrl.replace(/\/$/, '') + '/api')
        : apiUrl;

      const { supabaseAuth } = await import('./supabaseAuth');
      const token = await supabaseAuth.getAccessToken();
      if (!token) {
        return {
          success: false,
          error: 'Please sign in to activate your subscription.'
        };
      }

      const response = await fetch(`${apiBase}/verify-payment/${encodeURIComponent(sessionId)}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to verify payment' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const { verified, subscriptionId, customerId, planType, months } = await response.json();
      
      if (!verified) {
        throw new Error('Payment verification failed');
      }

      logger.info('Payment verified:', sessionId, subscriptionId, planType);

      // Sync subscription to Supabase so it survives localStorage clears
      const currentUserStr = localStorage.getItem('currentUser');
      if (currentUserStr) {
        try {
          const currentUser = JSON.parse(currentUserStr);
          if (currentUser.id) {
            syncSubscriptionToSupabase(
              currentUser.id,
              planType || 'monthly',
              subscriptionId || null,
              customerId || null,
              months || 1
            ).catch(err => logger.warn('Background subscription sync failed:', err));
          }
        } catch (e) {
          logger.warn('Could not sync subscription — user parse error');
        }
      }

      return {
        success: true,
        subscriptionId: subscriptionId || sessionId,
        planType: planType,
        months: months
      };
    } catch (error: any) {
      logger.error('Payment verification error:', error);
      return {
        success: false,
        error: error.message || 'Payment verification failed.'
      };
    }
  }

  // Check if Stripe is configured
  isConfigured(): boolean {
    return !!this.stripePublishableKey;
  }
}

export const paymentService = new PaymentService();
